/**
 * CADINU Verified Deploy: artifact parser and validator.
 *
 * An uploaded artifact is data, never code. This module reads it with JSON.parse only, after a size check and a nesting-depth
 * scan, and copies the few fields the deploy needs into fresh objects. Every read is an own-property read (`own()`), so keys like
 * `__proto__`, `constructor` or `toString` in the file can neither pollute a prototype nor be mistaken for data. Anything that
 * does not match the expected shape is rejected with a plain-text reason.
 *
 * Shapes accepted: Foundry (forge) `out/<File>.sol/<Name>.json`, Hardhat `artifacts/.../<Name>.json` (hh-sol-artifact-1),
 * Truffle `build/contracts/<Name>.json` and one contract of solc standard-JSON output (`{ abi, evm: { bytecode, deployedBytecode } }`).
 */

export const MAX_FILE_BYTES = 5 * 1024 * 1024
export const MAX_DEPTH = 64
const MAX_ABI_ENTRIES = 2000
const MAX_PARAMS = 64
const MAX_TUPLE_DEPTH = 8
const MAX_LIBRARIES = 64
const MAX_OFFSETS = 256
const MAX_IMMUTABLE_RANGES = 512

export class ArtifactError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ArtifactError'
  }
}

export type AbiParam = { name: string; type: string; components?: AbiParam[] }
export type Range = { start: number; length: number }
export type LinkRef = { key: string; file: string; name: string; offsets: Range[] }
export type ImmutableInfo = { status: 'listed' | 'none' | 'unknown'; ranges: Range[] }
export type ArtifactFormat = 'forge' | 'hardhat' | 'truffle' | 'solc'

export type Artifact = {
  name: string
  nameFrom: 'artifact' | 'compilationTarget' | 'file' | 'default'
  sourcePath: string | null
  format: ArtifactFormat
  compiler: string | null
  functionCount: number
  eventCount: number
  ctorInputs: AbiParam[]
  ctorPayable: boolean
  /** creation bytecode, lower-case hex without 0x; library placeholders still in place */
  creation: string
  /** expected runtime bytecode, lower-case hex without 0x, or null when the artifact has none */
  runtime: string | null
  creationLinks: LinkRef[]
  runtimeLinks: LinkRef[]
  immutables: ImmutableInfo
  /** library keys ("file:Name") the user must give an address for, creation and runtime merged */
  libraries: { key: string; file: string; name: string }[]
  isLibrary: boolean
}

const hasOwn = (o: object, k: string) => Object.prototype.hasOwnProperty.call(o, k)
/** Own-property read: never follows the prototype chain (so "constructor", "__proto__", "toString" read as absent unless really present). */
export function own(o: unknown, k: string): unknown {
  if (!isPlainObject(o)) return undefined
  return hasOwn(o, k) ? (o as Record<string, unknown>)[k] : undefined
}
export function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}
const ownKeys = (o: Record<string, unknown>) => Object.keys(o)

const IDENT = /^[A-Za-z_$][A-Za-z0-9_$]{0,127}$/
export const isIdentifier = (s: unknown): s is string => typeof s === 'string' && IDENT.test(s)

/**
 * Linear scan of the raw text for the deepest object/array nesting, ignoring brackets inside strings. Runs before JSON.parse so a
 * hostile file cannot exhaust the parser's stack; returns as soon as the limit is crossed.
 */
export function nestingDepthExceeds(text: string, limit = MAX_DEPTH): boolean {
  let depth = 0
  let inStr = false
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i)
    if (inStr) {
      if (c === 92 /* \ */) i++
      else if (c === 34 /* " */) inStr = false
      continue
    }
    if (c === 34) inStr = true
    else if (c === 123 || c === 91) {
      depth++
      if (depth > limit) return true
    } else if (c === 125 || c === 93) depth--
  }
  return false
}

/** JSON.parse behind the size and depth guards. Never evaluates anything. */
export function safeJsonParse(text: string, maxBytes = MAX_FILE_BYTES, maxDepth = MAX_DEPTH): unknown {
  if (typeof text !== 'string') throw new ArtifactError('The file could not be read as text.')
  if (text.length > maxBytes) throw new ArtifactError(`The file is larger than ${Math.round(maxBytes / 1024 / 1024)} MB.`)
  if (nestingDepthExceeds(text, maxDepth)) throw new ArtifactError(`The JSON is nested deeper than ${maxDepth} levels; an artifact never is.`)
  try {
    return JSON.parse(text)
  } catch {
    throw new ArtifactError('The file is not valid JSON.')
  }
}

// ---- ABI ------------------------------------------------------------------------------------------------------------

const ELEMENTARY = /^(address|bool|string|bytes([1-9]|[12][0-9]|3[0-2])?|u?int([1-9][0-9]{0,2})?|tuple)((\[[0-9]{0,6}\])*)$/

/** A Solidity ABI type string we can encode: elementary types, tuples, fixed and dynamic arrays. */
export function isAbiType(t: unknown): t is string {
  if (typeof t !== 'string' || t.length > 64) return false
  const m = ELEMENTARY.exec(t)
  if (!m) return false
  const intSize = m[3]
  if (intSize !== undefined) {
    const n = Number(intSize)
    if (n < 8 || n > 256 || n % 8 !== 0) return false
  }
  const dims = m[4].match(/\[([0-9]*)\]/g) ?? []
  if (dims.length > 4) return false
  return dims.every((d) => d === '[]' || (Number(d.slice(1, -1)) > 0 && !d.startsWith('[0')))
}

function readParams(raw: unknown, where: string, depth = 0): AbiParam[] {
  if (raw === undefined) return []
  if (!Array.isArray(raw)) throw new ArtifactError(`${where}: inputs must be a list.`)
  if (raw.length > MAX_PARAMS) throw new ArtifactError(`${where}: more than ${MAX_PARAMS} parameters.`)
  if (depth > MAX_TUPLE_DEPTH) throw new ArtifactError(`${where}: tuples nested deeper than ${MAX_TUPLE_DEPTH}.`)
  return raw.map((p, i) => {
    if (!isPlainObject(p)) throw new ArtifactError(`${where}: parameter ${i + 1} is not an object.`)
    const name = own(p, 'name') ?? ''
    const type = own(p, 'type')
    if (name !== '' && !isIdentifier(name)) throw new ArtifactError(`${where}: parameter ${i + 1} has a name that is not a Solidity identifier.`)
    if (!isAbiType(type)) throw new ArtifactError(`${where}: parameter ${i + 1} has an unsupported type.`)
    const out: AbiParam = { name: name as string, type }
    if (type.startsWith('tuple')) {
      const comps = readParams(own(p, 'components'), `${where} (tuple ${i + 1})`, depth + 1)
      if (comps.length === 0) throw new ArtifactError(`${where}: tuple parameter ${i + 1} has no components.`)
      out.components = comps
    }
    return out
  })
}

const ABI_TYPES = new Set(['function', 'constructor', 'event', 'error', 'fallback', 'receive'])

function readAbi(raw: unknown) {
  if (!Array.isArray(raw)) throw new ArtifactError('The artifact has no ABI list ("abi" must be an array).')
  if (raw.length > MAX_ABI_ENTRIES) throw new ArtifactError(`The ABI has more than ${MAX_ABI_ENTRIES} entries.`)
  let ctor: { inputs: AbiParam[]; payable: boolean } | null = null
  let functionCount = 0
  let eventCount = 0
  raw.forEach((e, i) => {
    if (!isPlainObject(e)) throw new ArtifactError(`ABI entry ${i + 1} is not an object.`)
    const type = own(e, 'type') ?? 'function'
    if (typeof type !== 'string' || !ABI_TYPES.has(type)) throw new ArtifactError(`ABI entry ${i + 1} has an unknown type.`)
    const name = own(e, 'name')
    if ((type === 'function' || type === 'event' || type === 'error') && !isIdentifier(name))
      throw new ArtifactError(`ABI entry ${i + 1} has a name that is not a Solidity identifier.`)
    if (type === 'constructor') {
      if (ctor) throw new ArtifactError('The ABI has more than one constructor.')
      const sm = own(e, 'stateMutability')
      const payable = sm === 'payable' || own(e, 'payable') === true
      ctor = { inputs: readParams(own(e, 'inputs'), 'Constructor'), payable }
    } else if (type === 'function') {
      functionCount++
      readParams(own(e, 'inputs'), `Function ${name as string}`)
      readParams(own(e, 'outputs'), `Function ${name as string}`)
    } else if (type === 'event') eventCount++
  })
  return { ctor: ctor ?? { inputs: [], payable: false }, functionCount, eventCount }
}

// ---- bytecode -------------------------------------------------------------------------------------------------------

function readRanges(raw: unknown, where: string, max: number, fixedLength?: number, maxLength = 32): Range[] {
  if (!Array.isArray(raw)) throw new ArtifactError(`${where}: offsets must be a list.`)
  if (raw.length === 0 || raw.length > max) throw new ArtifactError(`${where}: unexpected number of offsets.`)
  return raw.map((r) => {
    const start = own(r, 'start')
    const length = own(r, 'length')
    if (!Number.isSafeInteger(start) || (start as number) < 0) throw new ArtifactError(`${where}: an offset start is not a whole number.`)
    if (!Number.isSafeInteger(length) || (length as number) < 1 || (length as number) > maxLength)
      throw new ArtifactError(`${where}: an offset length is out of range.`)
    if (fixedLength !== undefined && length !== fixedLength) throw new ArtifactError(`${where}: a library link must be 20 bytes long.`)
    return { start: start as number, length: length as number }
  })
}

/** A source path: 1..300 characters, no control characters (C0, DEL). */
const PATH_OK = {
  test(p: string): boolean {
    if (p.length < 1 || p.length > 300) return false
    for (let i = 0; i < p.length; i++) {
      const c = p.charCodeAt(i)
      if (c < 0x20 || c === 0x7f) return false
    }
    return true
  },
}

function readLinkRefs(raw: unknown, where: string): LinkRef[] {
  if (raw === undefined || raw === null) return []
  if (!isPlainObject(raw)) throw new ArtifactError(`${where}: linkReferences must be an object.`)
  const out: LinkRef[] = []
  for (const file of ownKeys(raw)) {
    if (!PATH_OK.test(file)) throw new ArtifactError(`${where}: a library source path is empty, too long or has control characters.`)
    const libs = raw[file]
    if (!isPlainObject(libs)) throw new ArtifactError(`${where}: link references of a source file must be an object.`)
    for (const name of ownKeys(libs)) {
      if (!isIdentifier(name)) throw new ArtifactError(`${where}: a library name is not a Solidity identifier.`)
      out.push({ key: `${file}:${name}`, file, name, offsets: readRanges(libs[name], `${where} ${name}`, MAX_OFFSETS, 20) })
      if (out.length > MAX_LIBRARIES) throw new ArtifactError(`${where}: more than ${MAX_LIBRARIES} libraries.`)
    }
  }
  return out
}

/**
 * Normalises a bytecode string: optional 0x, even length, hex everywhere except inside the declared link ranges (which hold the
 * `__$...$__` placeholders). A placeholder that no link reference covers is rejected: the deploy would carry garbage bytes.
 */
export function readBytecode(raw: unknown, links: LinkRef[], where: string, allowEmpty: boolean): string {
  if (typeof raw !== 'string') throw new ArtifactError(`${where} is not a string.`)
  let hex = raw.startsWith('0x') || raw.startsWith('0X') ? raw.slice(2) : raw
  if (hex.length === 0) {
    if (allowEmpty) return ''
    throw new ArtifactError(`${where} is empty (an interface or abstract contract cannot be deployed).`)
  }
  if (hex.length % 2 !== 0) throw new ArtifactError(`${where} has an odd number of hex digits.`)
  const bytes = hex.length / 2
  const chars = hex.split('')
  for (const l of links)
    for (const r of l.offsets) {
      if (r.start + r.length > bytes) throw new ArtifactError(`${where}: a link reference for ${l.name} points past the end of the bytecode.`)
      if (chars[r.start * 2] !== '_' || chars[r.start * 2 + 1] !== '_')
        throw new ArtifactError(`${where}: the link reference for ${l.name} does not point at a library placeholder.`)
      for (let i = r.start * 2; i < (r.start + r.length) * 2; i++) chars[i] = '0'
    }
  const masked = chars.join('')
  if (masked.includes('_')) throw new ArtifactError(`${where} has a library placeholder that no linkReferences entry describes.`)
  if (!/^[0-9a-fA-F]*$/.test(masked)) throw new ArtifactError(`${where} contains characters that are not hex.`)
  hex = hex.toLowerCase()
  return hex
}

function readImmutables(raw: unknown, runtimeBytes: number, known: boolean): ImmutableInfo {
  if (raw === undefined || raw === null) return { status: known ? 'none' : 'unknown', ranges: [] }
  if (!isPlainObject(raw)) throw new ArtifactError('immutableReferences must be an object.')
  const ranges: Range[] = []
  for (const id of ownKeys(raw)) {
    if (!/^[0-9]{1,9}$/.test(id)) throw new ArtifactError('immutableReferences has a key that is not an AST id.')
    for (const r of readRanges(raw[id], 'immutableReferences', MAX_IMMUTABLE_RANGES)) {
      if (r.start + r.length > runtimeBytes) throw new ArtifactError('immutableReferences points past the end of the runtime bytecode.')
      ranges.push(r)
    }
    if (ranges.length > MAX_IMMUTABLE_RANGES) throw new ArtifactError('immutableReferences lists too many slots.')
  }
  return { status: ranges.length ? 'listed' : 'none', ranges }
}

// ---- shape detection --------------------------------------------------------------------------------------------------

type Raw = {
  format: ArtifactFormat
  name: unknown
  creation: unknown
  runtime: unknown
  creationLinks: unknown
  runtimeLinks: unknown
  immutables: unknown
  immutablesKnown: boolean
}

function detect(a: Record<string, unknown>): Raw {
  const bc = own(a, 'bytecode')
  const dbc = own(a, 'deployedBytecode')
  const evm = own(a, 'evm')
  if (isPlainObject(bc)) {
    // Foundry: bytecode/deployedBytecode are { object, linkReferences, immutableReferences? }
    return {
      format: 'forge',
      name: undefined,
      creation: own(bc, 'object'),
      runtime: isPlainObject(dbc) ? own(dbc, 'object') : undefined,
      creationLinks: own(bc, 'linkReferences'),
      runtimeLinks: isPlainObject(dbc) ? own(dbc, 'linkReferences') : undefined,
      immutables: isPlainObject(dbc) ? own(dbc, 'immutableReferences') : undefined,
      // forge leaves immutableReferences out when the contract has none
      immutablesKnown: isPlainObject(dbc),
    }
  }
  if (typeof bc === 'string') {
    const hh = own(a, '_format') === 'hh-sol-artifact-1' || own(a, 'deployedLinkReferences') !== undefined
    return {
      format: hh ? 'hardhat' : 'truffle',
      name: own(a, 'contractName'),
      creation: bc,
      runtime: dbc,
      creationLinks: own(a, 'linkReferences'),
      runtimeLinks: own(a, 'deployedLinkReferences'),
      immutables: own(a, 'immutableReferences'),
      immutablesKnown: false,
    }
  }
  if (isPlainObject(evm)) {
    const b = own(evm, 'bytecode')
    const d = own(evm, 'deployedBytecode')
    return {
      format: 'solc',
      name: own(a, 'contractName'),
      creation: own(b, 'object'),
      runtime: own(d, 'object'),
      creationLinks: own(b, 'linkReferences'),
      runtimeLinks: own(d, 'linkReferences'),
      immutables: own(d, 'immutableReferences'),
      immutablesKnown: isPlainObject(d) && own(d, 'immutableReferences') !== undefined,
    }
  }
  throw new ArtifactError('No creation bytecode found. Expected a Foundry, Hardhat or Truffle artifact (bytecode.object or bytecode string).')
}

function compilationTarget(a: Record<string, unknown>): { name: string; path: string } | null {
  let meta = own(a, 'metadata')
  if (typeof meta === 'string') {
    // Hardhat/solc keep metadata as a JSON string; parse it with the same guards, ignore it if it is not JSON
    try {
      meta = safeJsonParse(meta, 2 * 1024 * 1024, 32)
    } catch {
      return null
    }
  }
  const target = own(own(meta, 'settings'), 'compilationTarget')
  if (!isPlainObject(target)) return null
  const keys = ownKeys(target)
  if (keys.length !== 1) return null
  const name = target[keys[0]]
  return isIdentifier(name) && PATH_OK.test(keys[0]) ? { name, path: keys[0] } : null
}

function compilerVersion(a: Record<string, unknown>): string | null {
  let meta = own(a, 'metadata')
  if (typeof meta === 'string') {
    try {
      meta = safeJsonParse(meta, 2 * 1024 * 1024, 32)
    } catch {
      meta = undefined
    }
  }
  const v = own(own(meta, 'compiler'), 'version') ?? own(own(a, 'compiler'), 'version')
  return typeof v === 'string' && /^[0-9A-Za-z.+-]{1,64}$/.test(v) ? v : null
}

const LIB_RUNTIME = /^73[0]{40}3014/

/** Parses and validates an artifact object (already JSON-parsed). Throws ArtifactError with a readable reason. */
export function readArtifact(parsed: unknown, fileName = ''): Artifact {
  if (!isPlainObject(parsed)) throw new ArtifactError('The JSON must be an object (an artifact), not a list or a value.')
  const raw = detect(parsed)
  const { ctor, functionCount, eventCount } = readAbi(own(parsed, 'abi'))

  const creationLinks = readLinkRefs(raw.creationLinks, 'Creation bytecode')
  const runtimeLinks = readLinkRefs(raw.runtimeLinks, 'Runtime bytecode')
  const creation = readBytecode(raw.creation, creationLinks, 'The creation bytecode', false)
  let runtime: string | null = null
  if (raw.runtime !== undefined && raw.runtime !== null) {
    const r = readBytecode(raw.runtime, runtimeLinks, 'The runtime (deployed) bytecode', true)
    runtime = r.length ? r : null
  }
  const immutables = runtime ? readImmutables(raw.immutables, runtime.length / 2, raw.immutablesKnown) : { status: 'unknown' as const, ranges: [] }

  // a link in the runtime must also exist in the creation code (it is the same library address)
  const byKey = new Map<string, { key: string; file: string; name: string }>()
  for (const l of [...creationLinks, ...runtimeLinks]) if (!byKey.has(l.key)) byKey.set(l.key, { key: l.key, file: l.file, name: l.name })

  let name: string
  let nameFrom: Artifact['nameFrom']
  let sourcePath: string | null = typeof own(parsed, 'sourceName') === 'string' && PATH_OK.test(own(parsed, 'sourceName') as string) ? (own(parsed, 'sourceName') as string) : null
  const target = compilationTarget(parsed)
  if (raw.name !== undefined) {
    if (!isIdentifier(raw.name)) throw new ArtifactError('contractName is not a Solidity identifier.')
    name = raw.name
    nameFrom = 'artifact'
  } else if (target) {
    name = target.name
    nameFrom = 'compilationTarget'
    sourcePath = sourcePath ?? target.path
  } else {
    const base = fileName.replace(/^.*[\\/]/, '').replace(/\.json$/i, '')
    if (isIdentifier(base)) {
      name = base
      nameFrom = 'file'
    } else {
      name = 'Contract'
      nameFrom = 'default'
    }
  }

  return {
    name,
    nameFrom,
    sourcePath,
    format: raw.format,
    compiler: compilerVersion(parsed),
    functionCount,
    eventCount,
    ctorInputs: ctor.inputs,
    ctorPayable: ctor.payable,
    creation,
    runtime,
    creationLinks,
    runtimeLinks,
    immutables,
    libraries: [...byKey.values()],
    isLibrary: runtime !== null && LIB_RUNTIME.test(runtime),
  }
}

/** Text of an uploaded file to a validated artifact. */
export function parseArtifactText(text: string, fileName = ''): Artifact {
  return readArtifact(safeJsonParse(text), fileName)
}
